import { namespace, extend } from 'wsm-tagging-utils';

const runShiftEventHandler = (win) => {
	namespace(win, 'DDC.tracking.shift.handlers');

	const {
		DDC: { tracking }
	} = win;

	const buyNowOrCarBravoAttributes = {
		GMBUYSRP: {
			param: 'gmbuysrp',
			location: 'SRP',
			linkDestination: 'GMBuy'
		},
		GMBUYVDP: {
			param: 'gmbuyvdp',
			location: 'VDP',
			linkDestination: 'GMBuy'
		},
		GMBUYNAV: {
			param: 'gmbuynav',
			location: 'Global Nav',
			linkDestination: 'GMBuy'
		},
		CARBRAVOSRP: {
			param: 'carbravosrp',
			location: 'SRP',
			linkDestination: 'CarBravo'
		},
		CARBRAVOVDP: {
			param: 'carbravovdp',
			location: 'VDP',
			linkDestination: 'CarBravo'
		},
		CARBRAVONAV: {
			param: 'carbravonav',
			location: 'Global Nav',
			linkDestination: 'CarBravo'
		}
	};

	/**
	 * The standard EventHandler implementation that communicates with
	 * Shift Digital using the 'Shift Protocol'
	 * @param {Object} window an instance of the global window object
	 *
	 * @param {tracking.framework.runtime.Page} page A Page instance for page related queries
	 * @constructor
	 */
	tracking.shift.handlers.ShiftEventHandler = function ShiftEventHandler(
		window,
		page,
		form
	) {
		tracking.shift.handlers.AbstractEventHandler.call(
			this,
			window,
			page,
			form
		);
		this.accelerateInitialCalcStart = false;
		this.accelerateLeadFormStart = false;
	};

	// Make ShiftEventHandler inherit from AbstractEventHandler
	extend(
		tracking.shift.handlers.ShiftEventHandler,
		tracking.shift.handlers.AbstractEventHandler
	);

	tracking.shift.handlers.ShiftEventHandler.prototype.setFilterValueToSend =
		function setFilterValueToSend(data, delimiter) {
			const modifiedData = !data ? 'All' : data;
			return modifiedData.replace
				? modifiedData.replace(/,/g, delimiter)
				: modifiedData;
		};

	tracking.shift.handlers.ShiftEventHandler.prototype.logCategory =
		'ws-tagging:ShiftEventHandler';

	tracking.shift.handlers.ShiftEventHandler.prototype.getVehicle =
		function getVehicle(element) {
			let formVehicle = {};

			const vehicles =
				(this.page.dataLayerProperty('vehicles')?.length &&
					this.page.dataLayerProperty('vehicles')) ||
				(this.page.dataLayerProperty('confirmation.vehicle') && [
					this.page.dataLayerProperty('confirmation.vehicle')
				]) ||
				[];

			if (vehicles.length > 0) {
				let vehicleId =
					this.page.dataLayerProperty('confirmation.vehicle')?.uuid ||
					this.page.dataLayerProperty('vehicles.0')?.uuid;

				if (element) {
					const elementToQuery = element.closest('form');
					const vehicleIdInput =
						elementToQuery?.querySelector(
							'[data-id="vehicleId"]'
						) ||
						elementToQuery?.querySelector('[data-id="itemId"]');

					if (vehicleIdInput) {
						vehicleId = vehicleIdInput.value;
					}
				}

				if (vehicleId) {
					const {
						inventoryType,
						modelYear,
						make,
						model,
						trim,
						engine,
						transmission,
						interiorColor,
						exteriorColor,
						vin,
						internetPrice,
						msrp,
						fuelType,
						bodyStyle,
						driveLine,
						certified,
						stockNumber,
						newOrUsed,
						status
					} = vehicles.filter((obj) => obj.uuid === vehicleId)[0];

					formVehicle = {
						status: this.mapInventoryType(
							inventoryType,
							newOrUsed,
							certified
						),
						year: modelYear,
						make,
						model,
						trim,
						engine,
						transmission,
						interiorColor,
						exteriorColor,
						vin,
						msrp,
						displayedPrice: internetPrice,
						fuelType,
						bodyStyle,
						driveTrain: driveLine,
						stockNumber
					};

					if (
						this.stockStatusTagging &&
						this.page.isVehicleDetailPage()
					) {
						formVehicle.stockStatus = {
							Live: 'OnLot',
							'In Transit': 'InTransit',
							'In Transit At Factory': 'BeingBuilt'
						}[status];
					}
				}
			}

			return formVehicle;
		};

	tracking.shift.handlers.ShiftEventHandler.prototype.getBuyNowOrCarBravoAttributes =
		function getBuyNowOrCarBravoAttributes(url) {
			return Object.values(buyNowOrCarBravoAttributes).find(
				(buyNowOrCarBravoAttribute) =>
					url?.includes(buyNowOrCarBravoAttribute.param)
			);
		};

	// ======================================================================
	// ------------------------- Page Data Building -------------------------
	// ======================================================================

	/**
	 * Builds the data object to send Shift on a page view event.
	 *
	 * @param {string} alias the DDC page alias of the page being viewed
	 * @returns {Object} The shift data object to send
	 */
	tracking.shift.handlers.ShiftEventHandler.prototype.buildDataForPageView =
		function buildDataForPageView(aliasIn) {
			const alias =
				aliasIn ||
				this.page.dataLayerProperty('page.pageInfo.pageName'); // alias may come in undefined, add safety logic
			const locale = this.page.dataLayerProperty(
				'page.attributes.locale'
			);

			const data = {
				pageBrand: this.mapPageBrand(),
				pageType: this.pageName,
				websiteTier: this.page
					.dataLayerProperty('site.siteInfo.tier')
					.replace('tier', 'Tier '),
				language:
					locale.indexOf('_') > -1
						? locale.substr(0, locale.indexOf('_'))
						: locale,
				dealerZipCode: this.page.dataLayerProperty(
					'dealership.postalCode'
				),
				dealerState: this.page.dataLayerProperty(
					'dealership.stateProvince'
				),
				sessionId: sessionStorage.getItem('userSessionId'),
				dealerCity: this.page.dataLayerProperty('dealership.city'),
				dealerName: this.page.dataLayerProperty(
					'dealership.dealershipName'
				),
				userZipCode: this.page.dataLayerProperty('user.userPostalCode'),
				userCity: this.page.dataLayerProperty('user.userCity'),
				userState: this.page.dataLayerProperty('user.userState'),
				siteTechnologyVersion: this.page.dataLayerProperty(
					'site.design.variationId'
				)
			};

			if (this.page.isVehicleDetailPage()) {
				data.vehicleDetails = this.getVehicle();
			}

			if (this.page.isErrorPage(alias)) {
				data.errorCode = '404';
			}

			// DealerAreaCode is set for Toyota/Lexus and GM Canada
			if (this.dealerAreaCode) {
				if (this.mapPageBrand().toLowerCase() === 'lexus') {
					data.dealerLDA = this.dealerAreaCode;
				} else if (this.dealerCodeType.toLowerCase() === 'gmus') {
					data.dealerBac = this.retailerId;
				} else {
					data.dealerTDA = this.dealerAreaCode;
				}
			}

			return data;
		};

	/**
	 * Handles the page view event.
	 *
	 * @param {Object} eventData The event data object Relevant properties:
	 * {string} alias The DDC page alias
	 */

	tracking.shift.handlers.ShiftEventHandler.prototype.handlePageView =
		function handlePageView(eventData) {
			// assuming ws-inv-listing and being 'featuredVehiclePage' never happen at the same time
			if (!this.page.widgetIsPresent(/ws-inv-listing/)) {
				tracking.shift.handlers.AbstractEventHandler.prototype.handlePageView.call(
					this,
					eventData
				);
			}

			// custom filterSearch event: US687839
			if (this.isFeaturedVehiclePage) {
				let alias;

				if (eventData.alias === 'INVENTORY_FEATURED_NEW') {
					alias = 'New';
				} else if (eventData.alias === 'INVENTORY_FEATURED_USED') {
					alias = 'Used';
				} else if (eventData.alias.includes('CERTIFIED')) {
					alias = 'CPO';
				} else {
					alias = 'Mixed';
				}
				this.sendData(
					{
						'filterSearch.status': alias,
						events: 'filterSearch'
					},
					'filterSearch'
				);
			}
		};

	// ======================================================================
	// -------------------------- Send Events to Shift ----------------------
	// ======================================================================
	/**
	 * Handles the typed search event. ddc.form.typedSearch
	 *
	 * @param {Object} eventData The event data object.
	 *
	 * Relevant eventData properties:
	 * {string} trackingId The DDC racking identifier of the form
	 * {string} content The typed search content
	 */
	tracking.shift.handlers.ShiftEventHandler.prototype.handleTypedSearch =
		async function handleTypedSearch(eventData) {
			let formType;
			const { typedSearchContent, trackingId } = eventData;

			if (this.hasInventoryWidget()) {
				try {
					formType = await this.form.getFormMapping(trackingId);
				} catch (error) {
					this.log.error(error);
				}

				this.sendData({
					formType,
					typedSearchContent,
					events: 'typedSearch'
				});
			}
		};

	/**
	 * Handles the form confirmation event.
	 *
	 * @param {Object} eventData The event data object. Relevant properties:
	 * {string} trackingId The DDC tracking identifier of the form
	 */
	tracking.shift.handlers.ShiftEventHandler.prototype.handleFormConfirmation =
		async function handleFormConfirmation(eventData) {
			const { trackingId = 'DEFAULT', params, isModal } = eventData;

			const trackingIdToUse = trackingId || 'DEFAULT';

			let formType;
			try {
				formType = await this.form.getFormMapping(trackingIdToUse);
			} catch (error) {
				this.log.error(error);
			}

			const acceptedPrefContact = ['EMAIL', 'PHONE', 'TEXT'];
			const data = {
				sessionId: sessionStorage.getItem('userSessionId'),
				formType,
				leadType: 'lead',
				leadId: this.page.dataLayerProperty('confirmation.formEventId'),
				events: 'formSubmission',
				formVehicle: this.getVehicle(),
				displayType: this.formDisplayType(isModal)
			};

			const prefContact =
				(this.page.dataLayerProperty('confirmation.preferredContact') &&
					this.page
						.dataLayerProperty('confirmation.preferredContact')
						.toUpperCase()) ||
				null;

			data.prefContact =
				prefContact &&
				(acceptedPrefContact.indexOf(prefContact) > -1
					? prefContact
					: 'other');

			if (trackingId === 'TRADE_IN') {
				const tradeInData = params
					.split('&')
					.filter(
						(attr) =>
							attr.indexOf('year') > -1 ||
							attr.indexOf('make') > -1 ||
							attr.indexOf('model') > -1
					)
					.reduce((obj, attr) => {
						const keyRaw = attr.substr(0, attr.indexOf('='));
						let key = '';

						if (keyRaw.indexOf('year') > -1) {
							key = 'year';
						} else if (keyRaw.indexOf('make') > -1) {
							key = 'make';
						} else if (keyRaw.indexOf('model') > -1) {
							key = 'model';
						}

						const val = attr.substr(attr.indexOf('=') + 1);

						// eslint-disable-next-line no-param-reassign
						obj[key] = val;

						return obj;
					}, {});

				data.tradeIn = {
					year: tradeInData.year,
					make: tradeInData.make,
					model: tradeInData.model
				};
			}

			// the below consent stuff handles both GM cemConsent and Genesis endorsedCare conent form checkboxes
			// they both work a little different
			const cemConsentDefaultValue = sessionStorage.getItem(
				'cemConsentDefaultValue'
			);
			const cemConsent =
				win?.DDC?.WidgetData?.['ws-tracking']?.confirmationData?.form
					?.fields?.cemConsent;

			if (cemConsentDefaultValue != null && cemConsent != null) {
				if (
					cemConsent === 'YES_I_CONSENT' ||
					cemConsent === 'checked'
				) {
					data.formOptIn =
						cemConsentDefaultValue === 'true'
							? 'in-implicit'
							: 'in-explicit';
				} else {
					data.formOptIn = 'out';
				}

				sessionStorage.removeItem('cemConsentDefaultValue');
			}

			this.sendData(data);
		};

	/**
	 * Handles the form on page event.
	 *
	 * @param {Object} eventData The event data object
	 */
	tracking.shift.handlers.ShiftEventHandler.prototype.handleFormOnPage =
		async function handleFormOnPage(eventData) {
			const { isLead } = eventData;

			if (isLead) {
				const { element, isModal, trackingId } = eventData;

				let formType;

				try {
					formType = await this.form.getFormMapping(trackingId);
				} catch (error) {
					this.log.error(error);
				}

				const data = {
					formType,
					events: 'formShown',
					displayType: this.formDisplayType(isModal),
					formVehicle: this.getVehicle(element)
				};

				this.sendData(data);
			}
		};

	/**
	 * Handles the form initiation event.
	 *
	 * @param {Object} eventData The event data object. Relevant properties:
	 * {string} trackingId The DDC tracking identifier of the form
	 */
	tracking.shift.handlers.ShiftEventHandler.prototype.handleFormInitiation =
		async function handleFormInitiation(eventData) {
			const { element, isModal, trackingId } = eventData;
			const form = element.closest('form');
			let formType;
			try {
				formType = await this.form.getFormMapping(trackingId);
			} catch (error) {
				this.log.error(error);
			}

			const data = {
				formType,
				events: 'formInitiation',
				displayType: this.formDisplayType(isModal),
				formVehicle: this.getVehicle(element)
			};

			// if form has formOptIn field, get the default value of opt-in field
			// cemConsent: GM
			// endorsedCareADF: Genesis
			const cemConsent = form?.querySelector('[data-id="cemConsent"]');

			if (cemConsent) {
				sessionStorage.setItem(
					'cemConsentDefaultValue',
					cemConsent.defaultChecked
				);
			}

			const endorsedCare = form?.querySelector(
				'[data-id="endorsedCareADF"]'
			);

			if (endorsedCare) {
				sessionStorage.setItem(
					'cemConsentDefaultValue',
					endorsedCare.value === 'checked'
				);
			}

			this.sendData(data);
		};

	/**
	 * Handles the form on submission event.
	 *
	 * @param {Object} eventData The event data object
	 */
	tracking.shift.handlers.ShiftEventHandler.prototype.handleFormSubmission =
		async function handleFormSubmission(eventData) {
			const { validate, trackingId } = eventData;
			// yes, the not validate part is correct, client wants to know when form is not valid
			if (
				this.formErrorTagging &&
				typeof validate !== 'undefined' &&
				!validate &&
				trackingId !== 'INVENTORY_SEARCH'
			) {
				const { element, invalidFields = {}, isModal } = eventData;
				const formError = Object.entries(invalidFields)
					.map(
						([fieldName, fieldValue]) =>
							`${fieldName}: ${fieldValue}`
					)
					.join(', ');

				let formType;
				try {
					formType = await this.form.getFormMapping(trackingId);
				} catch (error) {
					this.log.error(error);
				}

				const data = {
					formType,
					events: 'formSubmissionError',
					displayType: this.formDisplayType(isModal),
					formVehicle: this.getVehicle(element),
					formError
				};

				this.sendData(data);
			}
		};

	/**
	 * Handles the offer click event.
	 *
	 * @param {Object} eventData The event data object.
	 *
	 * Relevant eventData properties to track for handleOfferClick:
	 * {string} id The offer identifier
	 * {string} name The name of the offer
	 */
	tracking.shift.handlers.ShiftEventHandler.prototype.handleOfferClick =
		function handleOfferClick(eventData) {
			const { incentives } =
				this.page.dataLayerProperty('page.attributes') || [];

			const applicableIncentive = incentives.filter(
				({ id }) => id === eventData.offerId
			);

			const offerClick = {
				offerId: eventData.offerId,
				offerName: eventData.offerName
			};

			if (applicableIncentive && applicableIncentive.length > 0) {
				const {
					type,
					currentVehicleModel,
					currentVehicleBodyStyle,
					applicableTrims,
					applicableYear,
					applicableMake
				} = applicableIncentive[0];

				let currentVehicleModelRevised = currentVehicleModel;
				if (
					applicableMake &&
					currentVehicleModel &&
					currentVehicleModel.match(applicableMake)
				) {
					currentVehicleModelRevised = currentVehicleModel.replace(
						`${applicableMake} `,
						''
					);
				}

				let currentVehicleTrims;

				if (applicableTrims && applicableTrims.length > 0) {
					currentVehicleTrims = applicableTrims
						.reduce((collection, trim) => {
							if (
								trim.match(
									`${applicableYear} ${applicableMake} ${currentVehicleModel} ${currentVehicleBodyStyle}`
								)
							) {
								const trimSubstr = trim.replace(
									`${applicableYear} ${applicableMake} ${currentVehicleModel} ${currentVehicleBodyStyle} `,
									''
								);
								return [...collection, trimSubstr];
							}
							return [...collection];
						}, [])
						.join('|');
				}

				const applicableIncentiveDataLayer = {
					offerType: (type && type.toLowerCase()) || null,
					vehicleModel: currentVehicleModelRevised,
					vehicleBodyStyle: currentVehicleBodyStyle,
					vehicleYear: applicableYear,
					vehicleTrim: currentVehicleTrims
				};

				Object.assign(offerClick, applicableIncentiveDataLayer);
			}

			this.sendData({
				offerClick,
				events: 'offerClick'
			});
		};

	/**
	 * Handles the filter search event.
	 *
	 * @param {Object} eventData The event data object.
	 *
	 * Relevant eventData properties to track for handleFilterSearch:
	 * {Number} resultCount The number of search results
	 * {Object} queryFacets The facets used in the search
	 */
	tracking.shift.handlers.ShiftEventHandler.prototype.handleFilterSearch =
		function handleFilterSearch(eventData) {
			if (this.hasInventoryWidget()) {
				const priceFilter =
					eventData.queryFacets.internetPrice ||
					eventData.queryFacets.msrp;

				const filterSearchData = {
					status: this.getInventoryType(),
					year: this.setFilterValueToSend(
						eventData.queryFacets.year,
						', '
					),
					make: this.setFilterValueToSend(
						eventData.queryFacets.make,
						', '
					),
					model: this.setFilterValueToSend(
						eventData.queryFacets.model,
						', '
					),
					trim: this.setFilterValueToSend(
						eventData.queryFacets.trim,
						', '
					),
					color: this.setFilterValueToSend(
						eventData.queryFacets.normalExteriorColor,
						', '
					),
					minPrice: priceFilter ? priceFilter.split('-')[0] : 'All',
					maxPrice: priceFilter ? priceFilter.split('-')[1] : 'All',
					bodyStyle: this.setFilterValueToSend(
						eventData.queryFacets.bodyStyle ||
							eventData.queryFacets.normalBodyStyle,
						', '
					),
					features: this.setFilterValueToSend(
						eventData.queryFacets.gvOption,
						' | '
					)
				};

				tracking.shift.handlers.AbstractEventHandler.prototype.handlePageView.call(
					this,
					{
						alias: win?.DDC?.dataLayer?.page?.pageInfo?.pageName,
						...eventData
					}
				);

				this.sendData({
					countSearchResults: eventData.resultCount,
					filterSearch: filterSearchData,
					events: 'filterSearch'
				});

				localStorage.setItem(
					'ShiftFilterEvents',
					JSON.stringify(eventData)
				);
			}
		};

	/**
	 * Handles the vehicle details view event.
	 * Applies all properties of VDP page view, but is handled as separate event.
	 *
	 * @param {Object} eventData The event data object.
	 * {Object} data The shift data object to send
	 */
	tracking.shift.handlers.ShiftEventHandler.prototype.handleVehicleDetailView =
		function handleVehicleDetailView(eventData) {
			const data = this.buildDataForPageView(eventData.alias);
			data.pageType = this.mapPageAlias();
			data.displayType = 'in-page';
			data.events = 'detailsView';
			this.sendData(data);
		};

	/**
	 * Handles get directions event.
	 *
	 * @param {Object} eventData The event data object
	 */
	tracking.shift.handlers.ShiftEventHandler.prototype.handleGetDirections =
		function handleGetDirections() {
			this.sendData({
				events: 'getDirections'
			});
		};

	/**
	 * ws-toyota-offers widget will send serviceOfferClick to the shift analytics with offer details.
	 * This event is being called from handleAllWidgetUserEvents which
	 *  will be called when widgetUserEvent is being published and widget name is ws-toyota-offers.
	 * @param eventData The event data object
	 */

	tracking.shift.handlers.ShiftEventHandler.prototype.handleServiceOfferClick =
		function handleServiceOfferClick(eventData) {
			if (eventData.widgetName === 'ws-toyota-offers') {
				const offerStatus =
					new Date(eventData.offerEndDate) > new Date()
						? 'active'
						: 'expired';
				const serviceOfferClick = {
					offerStatus,
					offerName: eventData.offerTitle,
					offerExpire: eventData.offerEndDate,
					offerType: eventData.offerType || null,
					offerCategory: eventData.offerCategory || null
				};
				this.sendData({
					serviceOfferClick,
					events: 'serviceOfferClick'
				});
			}
		};

	tracking.shift.handlers.ShiftEventHandler.prototype.handleAllWidgetUserEvents =
		function handleAllWidgetUserEvents(eventData) {
			switch (eventData.widgetName) {
				case 'ws-toyota-offers':
					this.handleServiceOfferClick(eventData);
					break;
				case 'ws-inv-listing':
					if (
						eventData.optionalData &&
						eventData.optionalData.isWindowStickerLink
					) {
						const dataToSend = {
							events: 'linkClick',
							linkText: eventData.elementCTA,
							linkType: 'CTA',
							location: 'content',
							pageType: 'Vehicle Listing'
						};

						if (
							eventData.optionalData.url.includes(
								'api/legacy/pse/windowsticker'
							)
						) {
							const { status, year, make, model, vin } =
								this.getVehicle();

							dataToSend.linkType = 'Monroney Label';
							dataToSend.vehicle = {
								status,
								year,
								make,
								model,
								vin
							};
						}
						this.sendData(dataToSend);
					}
					break;
				default:
					break;
			}
		};

	/**
	 * ddc-promotions tracking is set up to fire an event 'specials event' for all special categories
	 * we are setting up a listener for specialsEvent, but only want to handle the event if it has a value of 'service'
	 * for specialCategory for the service offer click event. Additionally, because our promotions tracking is set up to push
	 * multiple event types to the gtm data layer we need to make sure that we only have this sending out the click event
	 *
	 * @param {Object} eventData The event data object.
	 * {Object} serviceOfferClick The shift data object to send
	 */
	tracking.shift.handlers.ShiftEventHandler.prototype.handleSpecialsEvent =
		function handleSpecialsEvent(eventData) {
			let events;
			let properties;

			if (
				eventData?.specialCategory?.toLowerCase?.() === 'service' &&
				eventData.specialEvent === 'click'
			) {
				events = 'serviceOfferClick';
				const offerStatus =
					new Date(eventData.specialEndDate) > new Date()
						? 'active'
						: 'expired';
				properties = {
					[events]: {
						offerStatus,
						offerName: eventData.specialDescription,
						offerExpire: eventData.specialEndDate
					}
				};
			} else if (
				eventData?.widgetName === 'ws-specials' &&
				eventData?.specialType === 'click' &&
				eventData?.specialPlacementType === 'slideshow'
			) {
				events = 'carouselClick';
				properties = {
					[events]: {
						assetName: eventData?.oemId || eventData?.title || '',
						assetPosition: eventData?.promoIndex
					}
				};
			}

			if (events && properties) {
				this.sendData({
					...properties,
					events
				});
			}
		};

	tracking.shift.handlers.ShiftEventHandler.prototype.handleLinkClick =
		function handleLinkClick(eventData) {
			const { element, isTelephonicUrl, department, departmentId } =
				eventData;

			let dataToSend;

			if (
				element?.matches?.(
					'.navbar .nav-directions-button a, .page-header .adr a'
				) &&
				eventData.url.includes('directions')
			) {
				dataToSend = {
					events: 'getDirections'
				};
			} else if (isTelephonicUrl) {
				const approvedDepartments = ['Sales', 'Service', 'Parts'];
				const idPhoneMap = {
					SALES: 'Sales',
					SERVICE: 'Service',
					PARTS: 'Parts'
				};

				// Sometimes the id is listed in the department variable, which makes it in all caps
				const formattedDepartment = idPhoneMap[department]
					? idPhoneMap[department]
					: department;

				const correctDepartment =
					formattedDepartment &&
					approvedDepartments.includes(formattedDepartment)
						? formattedDepartment
						: idPhoneMap[departmentId];

				dataToSend = {
					clickToCallDepartment: correctDepartment || 'Other',
					events: 'clickToCall'
				};
			} else {
				const {
					attributes: {
						class: className,
						'data-src': dataSrc,
						'data-link-type': dataLinkType
					} = {},
					linkEvents = 'linkClick',
					location,
					text,
					trackingSection,
					url,
					widgetName
				} = eventData;

				let { linkType } = eventData;

				linkType = dataLinkType || linkType || 'CTA';

				let vehicleDetails;

				if (
					widgetName === 'ws-inv-listing' &&
					className?.includes?.('window-sticker-link')
				) {
					return;
				}

				if (
					(this.page.isVehicleDetailPage(
						this.page.currentPageAlias()
					) ||
						this.page.isVehicleListingPage(
							this.page.currentPageAlias()
						)) &&
					(url || dataSrc)?.includes('api/legacy/pse/windowsticker')
				) {
					const { status, year, make, model, vin } =
						this.getVehicle();

					linkType = 'Monroney Label';

					vehicleDetails = {
						status,
						year,
						make,
						model,
						vin
					};
				}

				let locationToSend = location;
				if (location === 'mainNav') {
					locationToSend =
						trackingSection === 'Footer' ? 'footer' : 'global nav';
				}

				if (linkType) {
					dataToSend = {
						events: linkEvents,
						...(linkType && { linkType }),
						...(locationToSend && { location: locationToSend }),
						...(this.pageName && { pageType: this.pageName }),
						...(vehicleDetails && { vehicleDetails }),
						...(['CTA', 'Monroney Label'].includes(linkType) && {
							linkText: text
						})
					};
				}

				const { location: locationBuyNowOrCarBravo, linkDestination } =
					this.getBuyNowOrCarBravoAttributes(url) || {};
				if (locationBuyNowOrCarBravo) {
					dataToSend.location = locationBuyNowOrCarBravo;
					dataToSend.linkDestination = linkDestination;
				}
			}

			if (dataToSend) {
				this.sendData(dataToSend);
			}
		};

	tracking.shift.handlers.ShiftEventHandler.prototype.handleFormChange =
		async function handleFormChange(eventData) {
			const { element, trackingId } = eventData;

			// formChangeTagging is always true so when we migrate this to the new tagging system we can remove this check
			if (trackingId && this.formChangeTagging) {
				const { isModal, fieldName } = eventData;

				let formType;
				try {
					formType = await this.form.getFormMapping(trackingId);
				} catch (error) {
					this.log.error(error);
				}

				this.sendData({
					formType,
					displayType: this.formDisplayType(isModal),
					events: 'formFieldInteraction',
					formFieldName: this.getFormFieldName(fieldName),
					formVehicle: this.getVehicle(element)
				});
			}
		};

	/**
	 * We want to handle a submit event in the search form.
	 *
	 * @param {Object} eventData The event data object. Relevant properties:
	 */
	tracking.shift.handlers.ShiftEventHandler.prototype.handleWidgetUserEvent =
		function handleWidgetUserEvent(eventData) {
			let events;
			let properties;

			if (
				this.hasInventoryWidget() &&
				eventData.widgetName === 'ws-inv-text-search'
			) {
				if (
					eventData.element === 'search form' &&
					eventData.result === 'form submitted'
				) {
					events = 'typedSearch';
					properties = {
						typedSearchContent: eventData.fieldValue
					};
				}
			}

			if (events && properties) {
				if (events && properties) {
					this.sendData({
						...properties,
						events
					});
				}
			}

			if (
				this.page.widgetIsPresent(/ws-inv-listing/) &&
				eventData.result === 'Results updated with sort selection'
			) {
				const filterData = localStorage.getItem('ShiftFilterEvents');
				this.handleFilterSearch(JSON.parse(filterData));
			}
		};

	tracking.shift.handlers.ShiftEventHandler.prototype.handleWalletUpdated =
		function handleWalletUpdated(_eventData) {
			// underscore to avoid linter error, if you need eventData in the future remove underscore and use it!
			this.sendData({
				events: 'myWalletClick'
			});
		};
};

export default runShiftEventHandler;
